// The signed-in Actor's profile, held once for every face that wears it
// (US-CAN-08): the dock's account chip, the Account page's session row and
// photo section. The Desk holds no live subscription to the Actor row —
// the profile door is a request over the loopback server and the entered
// World's grant — so this store reads it once per World and grant revision,
// re-reads before the photo's signed URL expires (the brand-mark law), lets a
// save publish its readback so every face flips at once, and forgets
// everything on sign-out so no previous account's name survives.
import { useEffect } from "react";
import { create } from "zustand";

import { readActorProfile, type ActorProfile } from "./profileClient";
import { useNeucleosWorldStore } from "./worldStore";

const RERESOLVE_MARGIN_MS = 30_000;
const RERESOLVE_MIN_DELAY_MS = 15_000;

interface ActorProfileEntry {
  /** null after a refusal: the face falls back to the sign-in identity. */
  readonly profile: ActorProfile | null;
  readonly grantRevision: number;
}

interface ActorProfileState {
  readonly byWorld: Readonly<Record<string, ActorProfileEntry>>;
}

export const useActorProfileStore = create<ActorProfileState>(() => ({ byWorld: {} }));

const inFlight = new Map<string, Promise<ActorProfile | null>>();

/** Which World's grant reads the profile: the entered World, else the first World holding a grant. */
export function selectProfileWorldKey(state: {
  readonly signedIn: boolean;
  readonly selection: { readonly status: string; readonly worldKey?: string | null } | null;
  readonly worlds: ReadonlyArray<{ readonly worldKey: string; readonly mcpTokenPresent: boolean }>;
}): string | null {
  if (!state.signedIn) return null;
  if (state.selection?.status === "selected" && typeof state.selection.worldKey === "string") {
    return state.selection.worldKey;
  }
  return state.worlds.find((world) => world.mcpTokenPresent)?.worldKey ?? null;
}

export function publishActorProfile(worldKey: string, profile: ActorProfile | null): void {
  const grantRevision = useNeucleosWorldStore.getState().grantRevision;
  useActorProfileStore.setState((state) => ({
    byWorld: { ...state.byWorld, [worldKey]: { profile, grantRevision } },
  }));
}

export function clearActorProfiles(): void {
  inFlight.clear();
  useActorProfileStore.setState({ byWorld: {} });
}

/** Read the profile through the door once; concurrent callers share the read. */
export function refreshActorProfile(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ActorProfile | null> {
  const pending = inFlight.get(worldKey);
  if (pending) return pending;
  const read = readActorProfile(worldKey, fetchImpl)
    .then((outcome) => {
      const profile = outcome.ok ? outcome.profile : null;
      publishActorProfile(worldKey, profile);
      return profile;
    })
    .catch(() => {
      publishActorProfile(worldKey, null);
      return null;
    })
    .finally(() => {
      if (inFlight.get(worldKey) === read) inFlight.delete(worldKey);
    });
  inFlight.set(worldKey, read);
  return read;
}

// Sign-out forgets every profile: the next account never inherits a face.
useNeucleosWorldStore.subscribe((state, previous) => {
  if (previous.signedIn && !state.signedIn) clearActorProfiles();
});

/** When a fresh read is due for a held entry: before the signed photo URL expires. */
export function nextProfileRefreshDelay(profile: ActorProfile | null, now: number): number | null {
  if (profile === null || profile.avatarUrlExpiresAt === null) return null;
  return Math.max(profile.avatarUrlExpiresAt - now - RERESOLVE_MARGIN_MS, RERESOLVE_MIN_DELAY_MS);
}

/**
 * The profile the given World's grant reads, or null while unread, refused,
 * or signed out. Reads once per (World, grant revision); a held entry whose
 * photo URL is about to expire is re-read on time.
 */
export function useActorProfile(worldKey: string | null): ActorProfile | null {
  const signedIn = useNeucleosWorldStore((state) => state.signedIn);
  const grantRevision = useNeucleosWorldStore((state) => state.grantRevision);
  const entry = useActorProfileStore((state) =>
    worldKey === null ? undefined : state.byWorld[worldKey],
  );
  const held = entry !== undefined && entry.grantRevision === grantRevision;
  const expiresAt = entry?.profile?.avatarUrlExpiresAt ?? null;

  useEffect(() => {
    if (worldKey === null || !signedIn) return;
    if (!held) {
      void refreshActorProfile(worldKey);
      return;
    }
    const delay = nextProfileRefreshDelay(entry.profile, Date.now());
    if (delay === null) return;
    const timer = setTimeout(() => void refreshActorProfile(worldKey), delay);
    return () => clearTimeout(timer);
    // `expiresAt` stands in for the entry's profile identity: a re-read
    // publishes a fresh expiry and re-arms the timer.
  }, [worldKey, signedIn, grantRevision, held, expiresAt, entry]);

  return signedIn && held ? entry.profile : null;
}
